/**
 * Password validation utility
 * Validates password requirements for signup and password reset
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validate password against requirements:
 * - Minimum 8 characters
 * - At least one number
 * - At least one special character
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push("Password must be at least 8 characters long");
  }

  if (!/\d/.test(password)) {
    errors.push("Password must contain at least one number");
  }

  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push("Password must contain at least one special character (!@#$%^&*(),.?\":{}|<>)");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate that password and confirm password match
 */
export function validatePasswordMatch(password: string, confirmPassword: string): { isValid: boolean; error?: string } {
  if (password !== confirmPassword) {
    return {
      isValid: false,
      error: "Passwords do not match",
    };
  }

  return { isValid: true };
}

/**
 * Get a single error message for password validation
 * Returns the first error, or empty string if valid
 */
export function getPasswordError(password: string): string {
  const result = validatePassword(password);
  return result.errors[0] || "";
}

/**
 * Validate both password requirements and matching
 */
export function validateNewPassword(password: string, confirmPassword: string): {
  isValid: boolean;
  error?: string;
} {
  // First check if passwords match
  const matchResult = validatePasswordMatch(password, confirmPassword);
  if (!matchResult.isValid) {
    return matchResult;
  }

  // Then validate password requirements
  const validationResult = validatePassword(password);
  if (!validationResult.isValid) {
    return {
      isValid: false,
      error: validationResult.errors[0],
    };
  }

  return { isValid: true };
}

/**
 * Check individual password requirements
 * Returns the status of each requirement for dynamic UI feedback
 */
export interface PasswordRequirementStatus {
  minLength: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

export function checkPasswordRequirements(password: string): PasswordRequirementStatus {
  return {
    minLength: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };
}
